import { CalendarMonth, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Tooltip,
  ClickAwayListener,
  Popper,
  TextField,
  Box,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function generateCalendar(year, month) {
  const dates = [];
  let curr = new Date(year, month, 1, 0, 0, 0, 0);
  while (curr.getMonth() === month) {
    dates.push(curr);
    curr = new Date(year, month, curr.getDate() + 1, 0, 0, 0, 0);
  }
  return { year: dates[0].getFullYear(), month: dates[0].getMonth(), dates };
}

function dateEquals(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function dateDiff(d1, d2) {
  const diff = Math.abs(d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24);
  return diff + 1;
}

function isBetween(d, d1, d2) {
  if (!d1 || !d2) return false;
  const t = d.getTime();
  const t1 = d1.getTime();
  const t2 = d2.getTime();
  return t1 < t2 ? t > t1 && t < t2 : t < t1 && t > t2;
}

function getDateRangeString(first, second) {
  if (dateEquals(first, second)) {
    return `${first.getDate()} ${monthNames[first.getMonth()]}, ${first.getFullYear()}`;
  }
  if (first.getTime() < second.getTime()) {
    return `${first.getDate()} ${
      monthNames[first.getMonth()]
    }, ${first.getFullYear()} - ${second.getDate()} ${
      monthNames[second.getMonth()]
    }, ${second.getFullYear()}`;
  }
  return `${second.getDate()} ${
    monthNames[second.getMonth()]
  }, ${second.getFullYear()} - ${first.getDate()} ${
    monthNames[first.getMonth()]
  }, ${first.getFullYear()}`;
}

export default function ITDateRangePicker(props) {
  const { value, onChange, placeholder, label, required, disabled } = props;
  const [activeMonth, setActiveMonth] = useState();
  const [selected, setSelected] = useState({});
  const [hoveredDate, setHoveredDate] = useState();
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  const closeAndCancel = useCallback(() => {
    setShowDropdown(false);
    if (value) {
      setSelected({ first: value.from, second: value.to });
    } else {
      setSelected({});
    }
  }, [value]);

  useEffect(() => {
    if (value) {
      setSelected({ first: value.from, second: value.to });
    }
  }, [value]);

  useEffect(() => {
    if (!showDropdown) setActiveMonth(undefined);
  }, [showDropdown]);

  const calendars = useMemo(() => {
    let date = activeMonth;
    if (!date) {
      date = value ? value.from : new Date(Date.now());
    }
    const year = date.getFullYear();
    const month = date.getMonth();
    return [
      generateCalendar(year, month),
      month === 11 ? generateCalendar(year + 1, 0) : generateCalendar(year, month + 1),
    ];
  }, [activeMonth, value]);

  const handleInputClick = useCallback(() => setShowDropdown((curr) => !curr), []);

  const handleNextMonth = useCallback(() => {
    if (activeMonth) {
      const year = activeMonth.getFullYear();
      const month = activeMonth.getMonth();
      setActiveMonth(new Date(year, month + 1, 1, 0, 0, 0, 0));
    } else {
      const now = value ? value.from : new Date(Date.now());
      const year = now.getFullYear();
      const month = now.getMonth();
      setActiveMonth(new Date(year, month + 1, 1, 0, 0, 0, 0));
    }
  }, [activeMonth, value]);

  const handlePrevMonth = useCallback(() => {
    if (activeMonth) {
      const year = activeMonth.getFullYear();
      const month = activeMonth.getMonth();
      setActiveMonth(new Date(year, month - 1, 1, 0, 0, 0, 0));
    } else {
      const now = value ? value.from : new Date(Date.now());
      const year = now.getFullYear();
      const month = now.getMonth();
      setActiveMonth(new Date(year, month - 1, 1, 0, 0, 0, 0));
    }
  }, [activeMonth, value]);

  const handleDateClick = useCallback(
    (date) => {
      if (selected.first && !selected.second) {
        setSelected({ ...selected, second: date });
        setHoveredDate(undefined);
      } else setSelected({ first: date });
    },
    [selected]
  );

  const handleDateMouseEnter = useCallback(
    (date) => {
      if (selected.first && !selected.second) setHoveredDate(date);
    },
    [selected]
  );

  const handleDateMouseLeave = useCallback(() => {
    if (selected.first && !selected.second) setHoveredDate(undefined);
  }, [selected]);

  const handleApply = useCallback(() => {
    setShowDropdown(false);
    if (!selected.first || !selected.second || !onChange) return;
    if (selected.first.getTime() < selected.second.getTime()) {
      onChange({
        from: selected.first,
        to: new Date(
          selected.second.getFullYear(),
          selected.second.getMonth(),
          selected.second.getDate(),
          23,
          59,
          59,
          999
        ),
      });
    } else {
      onChange({
        from: selected.second,
        to: new Date(
          selected.first.getFullYear(),
          selected.first.getMonth(),
          selected.first.getDate(),
          23,
          59,
          59,
          999
        ),
      });
    }
  }, [selected, onChange]);

  const getDateClass = useCallback(
    (date) => {
      const { first } = selected;
      const second = selected.second ?? hoveredDate;
      if (first && dateEquals(date, first)) {
        if (!second || dateEquals(first, second))
          return 'ITDateRangePicker__date ITDateRangePicker__date--active';
        if (date.getTime() > second.getTime()) {
          return 'ITDateRangePicker__date ITDateRangePicker__date--active-right';
        }
        return 'ITDateRangePicker__date ITDateRangePicker__date--active-left';
      }
      if (second && dateEquals(date, second)) {
        if (!first) return 'ITDateRangePicker__date ITDateRangePicker__date--active';
        if (date.getTime() > first.getTime()) {
          return 'ITDateRangePicker__date ITDateRangePicker__date--active-right';
        }
        return 'ITDateRangePicker__date ITDateRangePicker__date--active-left';
      }
      if (second && first && isBetween(date, first, second)) {
        return 'ITDateRangePicker__date ITDateRangePicker__date--between';
      }
      return 'ITDateRangePicker__date';
    },
    [hoveredDate, selected]
  );

  return (
    <div className="ITDateRangePicker" ref={wrapperRef}>
      <TextField
        className="ITDateRangePicker__form-group"
        disabled={disabled}
        required={required}
        label={label}
        InputProps={{
          readOnly: true,
          className: 'ITDateRangePicker__input',
          endAdornment: <CalendarMonth />,
        }}
        InputLabelProps={{ className: 'ITDateRangePicker__label' }}
        value={value ? getDateRangeString(value.from, value.to) : ''}
        placeholder={placeholder}
        onClick={handleInputClick}
      />
      <Popper
        open={showDropdown}
        anchorEl={() => wrapperRef.current}
        placement="bottom-start"
        className="ITDateRangePicker__popper">
        <ClickAwayListener onClickAway={closeAndCancel}>
          <Box className="ITDateRangePicker__box">
            <div className="ITDateRangePicker__main">
              {calendars.map((cal, i) => (
                <div key={`${cal.year}-${cal.month}`} className="ITDateRangePicker__calendar">
                  <div className="ITDateRangePicker__calendar-head">
                    {i === 0 ? (
                      <IconButton type="button" onClick={handlePrevMonth}>
                        <KeyboardArrowLeft />
                      </IconButton>
                    ) : (
                      <div />
                    )}
                    <div className="ITDateRangePicker__month">{`${monthNames[cal.month]} ${
                      cal.year
                    }`}</div>
                    {i === calendars.length - 1 ? (
                      <IconButton type="button" onClick={handleNextMonth}>
                        <KeyboardArrowRight />
                      </IconButton>
                    ) : (
                      <div />
                    )}
                  </div>
                  <div className="ITDateRangePicker__calendar-body">
                    <div className="ITDateRangePicker__days">
                      {dayNames.map((d) => (
                        <div key={d} className="ITDateRangePicker__day">
                          {d}
                        </div>
                      ))}
                    </div>
                    <div className="ITDateRangePicker__dates">
                      {new Array(cal.dates[0].getDay()).fill(0).map(() => (
                        <div />
                      ))}
                      {cal.dates.map((d) =>
                        selected.first && hoveredDate && dateEquals(d, hoveredDate) ? (
                          <Tooltip
                            title={`${dateDiff(selected.first, d)} ${
                              dateDiff(selected.first, d) === 1 ? 'day' : 'days'
                            }`}>
                            <button
                              type="button"
                              key={d.getTime()}
                              className={getDateClass(d)}
                              onClick={() => handleDateClick(d)}
                              onMouseEnter={() => handleDateMouseEnter(d)}
                              onMouseLeave={handleDateMouseLeave}>
                              {d.getDate()}
                            </button>
                          </Tooltip>
                        ) : (
                          <button
                            type="button"
                            key={d.getTime()}
                            className={getDateClass(d)}
                            onClick={() => handleDateClick(d)}
                            onMouseEnter={() => handleDateMouseEnter(d)}
                            onMouseLeave={handleDateMouseLeave}>
                            {d.getDate()}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ITDateRangePicker__dropdown-bottom">
              {selected.first && selected.second && (
                <div className="ITDateRangePicker__selected">
                  {getDateRangeString(selected.first, selected.second)}
                </div>
              )}
              <Button type="button" variant="outlined" onClick={closeAndCancel}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!selected.first || !selected.second}
                variant="contained"
                onClick={handleApply}>
                Apply
              </Button>
            </div>
          </Box>
        </ClickAwayListener>
      </Popper>
    </div>
  );
}

ITDateRangePicker.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

ITDateRangePicker.defaultProps = {
  value: null,
  onChange: () => {},
  placeholder: '',
  label: '',
  required: false,
  disabled: false,
};
